/** Profile indexability threshold — mirrors web/backend/includes/seo_utils.php */

export type ProfileSeoStats = {
  public_build_count: number;
  public_mod_count: number;
  public_builds_with_photo: number;
  bio_chars: number;
};

export const PROFILE_SEO_MIN_BIO_CHARS = 40;
export const PROFILE_SEO_MIN_PUBLIC_MODS = 3;

export function profileSeoStatsFromData(
  bio: string | null | undefined,
  vehicles: Array<{ is_public?: boolean; modification_count?: number; image_url?: string | null }>
): ProfileSeoStats {
  const publicBuilds = vehicles.filter((v) => v.is_public !== false);
  const bioText = (bio ?? '').trim();
  return {
    public_build_count: publicBuilds.length,
    public_mod_count: publicBuilds.reduce((sum, v) => sum + (v.modification_count ?? 0), 0),
    public_builds_with_photo: publicBuilds.filter((v) => Boolean(v.image_url)).length,
    bio_chars: bioText.length,
  };
}

export function profileMeetsIndexThreshold(stats: ProfileSeoStats): boolean {
  if (stats.public_build_count < 1) return false;
  if (stats.bio_chars >= PROFILE_SEO_MIN_BIO_CHARS) return true;
  if (stats.public_mod_count >= PROFILE_SEO_MIN_PUBLIC_MODS) return true;
  if (stats.public_builds_with_photo >= 1 && stats.public_mod_count >= 1) return true;
  return false;
}

export type ProfileSeoPayload = {
  indexable: boolean;
  title: string;
  description: string;
  canonical: string;
  image?: string;
  robots: string;
};

export function buildProfileSeo(input: {
  origin: string;
  username: string;
  displayName: string;
  bio?: string | null;
  avatarUrl?: string | null;
  vehicles: Array<{ is_public?: boolean; modification_count?: number; image_url?: string | null }>;
}): ProfileSeoPayload {
  const stats = profileSeoStatsFromData(input.bio, input.vehicles);
  const indexable = profileMeetsIndexThreshold(stats);
  const canonical = `${input.origin}/${encodeURIComponent(input.username)}`;
  const bio = (input.bio ?? '').trim();
  const description =
    bio.length > 0
      ? bio.length > 160
        ? `${bio.slice(0, 159)}…`
        : bio
      : `${stats.public_build_count} public build${stats.public_build_count === 1 ? '' : 's'} · ${stats.public_mod_count} documented modification${stats.public_mod_count === 1 ? '' : 's'} on Bilt:Spec.`;

  const image =
    input.avatarUrl ||
    input.vehicles.find((v) => v.is_public !== false && v.image_url)?.image_url ||
    '/images/brand/og-default.jpg';

  return {
    indexable,
    title: `${input.displayName} (@${input.username}) | Bilt:Spec`,
    description,
    canonical,
    image,
    robots: indexable ? 'index, follow' : 'noindex, nofollow',
  };
}
